export default
{
    computed:
    {
        isReducingValues()
        {
            return this.$props.reduce !== this.$options.props.reduce.default
        },

        isTrackingValues()
        {
            // Determine if the component needs to track the state of values internally. - @return {Boolean}

            return ( typeof this.value === 'undefined' || this.$options.propsData.hasOwnProperty('reduce') )
        },

        selectedValue()
        {
            // The options that are currently selected. - @return {Array}

            let value = this.modelValue;

            // Vue select has to manage value internally
            if( this.isTrackingValues ) { value = this.$data._value }

            if (value !== undefined && value !== null && value !== '') { return [].concat(value) }

            return [];
        },

        optionList()
        {
            // The options available to be chosen from the dropdown, including any tags that have been pushed. - @return {Array}

            return this.options.concat( this.pushTags ? this.pushedTags : [] );
        },

        searchEl()
        {
            // Find the search input DOM element. - @returns {HTMLInputElement}

            if( this.$slots['select-search-input'] )
            {
                return this.$refs.selectedOptions.querySelector( this.searchInputQuerySelector );
            }
            else
            {
                return this.$refs.search;
            }
        },

        scope()
        {
            // The object to be bound to the $slots.search scoped slot. - @returns {Object}

            const activedescendant =  {};

            if( this.dropdownOpen && this.filteredOptions[this.typeAheadPointer] )
            {
                activedescendant['aria-activedescendant'] = `vs${this.uid}__option-${this.typeAheadPointer}`;
            }

            return (
            {
                select:
                {
                    attributes:
                    {
                        dir  : this.dir,

                        class:
                        {
                            'vs--open'          : this.dropdownOpen,
                            'vs--single'        : !this.multiple,
                            'vs--multiple'      : this.multiple,
                            'vs--searching'     : this.searching && !this.noDrop,
                            'vs--searchable'    : this.searchable && !this.noDrop,
                            'vs--unsearchable'  : !this.searchable,
                            'vs--loading'       : this.mutableLoading,
                            'vs--disabled'      : this.disabled,
                            [this.class]        : this.class
                        }
                    },

                    toggle:
                    {
                        attributes:
                        {
                            'ref'           : 'toggle',
                            'role'          : 'combobox',
                            'id'            : `vs${this.uid}__combobox`,
                            'aria-owns'     : `vs${this.uid}__listbox`,
                            'aria-label'    : 'Search for option',
                            'aria-expanded' : this.dropdownOpen.toString(),
                        },

                        listeners:
                        {
                            mousedown: this.toggleDropdown
                        },

                        search:
                        {
                            attributes:
                            {
                                'id'                : this.inputId,
                                'ref'               : 'search',
                                'type'              : 'search',
                                'value'             : this.search,
                                'placeholder'       : this.searchPlaceholder,
                                'disabled'          : this.disabled,
                                'tabindex'          : this.tabindex,
                                'readonly'          : !this.searchable,
                                'autocomplete'      : this.autocomplete,
                                'aria-autocomplete' : 'list',
                                'aria-labelledby'   : `vs${this.uid}__combobox`,
                                'aria-controls'     : `vs${this.uid}__listbox`,
                                ...activedescendant,
                            },

                            listeners:
                            {
                                compositionstart: () => ( this.isComposing = true ),
                                compositionend  : () => ( this.isComposing = false ),
                                keydown         : this.onSearchKeyDown,
                                blur            : this.onSearchBlur,
                                focus           : this.onSearchFocus,
                                input           : ( e ) => ( this.search = e.target.value ),
                            },
                        },

                        actions:
                        {
                            deselectIndicator:
                            {
                                attributes : { ref: 'clearButton', role: 'clear', disabled: this.disabled },
                                listeners  : { click: this.clearSelection }
                            },

                            spinnerIndicator:
                            {
                                attributes: { ref: 'spinnerIndicator', role: 'loading' }
                            },

                            openIndicator:
                            {
                                attributes: { ref: 'openIndicator', role: 'presentation' }
                            }
                        }
                    },

                    dropdown:
                    {
                        attributes:
                        {
                            'ref'      : 'dropdown',
                            'id'       : `vs${this.uid}__listbox`,
                            'key'      : `vs${this.uid}__listbox`,
                            'role'     : 'listbox',
                            'tabindex' : '-1',
                            'class'    :
                            {
                                [this.dropdownClass]: this.dropdownClass
                            }
                        },

                        listeners:
                        {
                            mousedown   : this.onMousedown,
                            mouseup     : this.onMouseUp
                        },

                        options:
                        {
                            empty:
                            {
                                attributes:
                                {
                                    search      : this.search,
                                    loading     : this.mutableLoading,
                                    searching   : this.searching
                                }
                            }
                        }
                    }
                }
            })
        },

        childComponents()
        {
            // Returns an object containing the child components that will be used throughout the component. The `component` prop can be used to overwrite the defaults. - @return {Object}

            return { ...this.components }
        },

        searching()
        {
            // Return the current state of the search input - @return {Boolean} True if non empty value

            return !!this.search;
        },

        dropdownOpen()
        {
            // Return the current state of the dropdown menu. - @return {Boolean} True if open

            return this.dropdownShouldOpen( this );
        },

        searchPlaceholder()
        {
            // Return the placeholder string if it's set & there is no value selected. - @return {String} Placeholder text

            return this.isValueEmpty && this.placeholder ? this.placeholder : undefined;
        },

        filteredOptions()
        {
            // The currently displayed options, filtered by the search elements value. If tagging true, the search text will be prepended if it doesn't already exist. - @return {array}

            const optionList = [].concat( this.optionList );

            if( !this.filterable && !this.taggable ) { return optionList }

            let options = this.search.length ? this.filter(optionList, this.search, this) : optionList;

            if( this.taggable && this.search.length )
            {
                const createdOption = this.createOption( this.search );

                if( !this.optionExists( createdOption ) ) { options.unshift( createdOption ) }
            }

            return options;
        },

        isValueEmpty()
        {
            // Check if there aren't any options selected. - @return {Boolean}

            return this.selectedValue.length === 0;
        },

        showClearButton()
        {
            // Determines if the clear button should be displayed. - @return {Boolean}

            return ( /*!this.multiple &&*/ !this.hideClearButton && this.clearable /*&& !this.open*/ && !this.isValueEmpty );
        },

        showIndicator()
        {
            // Determines if the clear button should be displayed. - @return {Boolean}

            return ( !this.hideIndicator && !this.noDrop );
        }
    }
}